import { useEffect, useState } from 'react';
import '../App.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import { PageList } from './PageList';
import { PageEditor } from './PageEditor';
import { TestBookends } from './TestBookends';
import { FunctionInputs, InputObj, InputValues, PageItem, PageObj, strippedInputObj } from '../Interfaces/TestCaseInterface';
import { useNavigate, useParams } from 'react-router-dom';
import cloneDeep from 'lodash/cloneDeep'
import { nanoid } from 'nanoid';
import { isEqual } from 'lodash';
import Typography from '@mui/material/Typography';

interface TestCaseEditorProps {
  tests: Record<string, Array<Array<strippedInputObj>>>,
  setTests: React.Dispatch<React.SetStateAction<Record<string, Array<Array<strippedInputObj>>>>>,
  policyVariables: Record<string, PageItem>,
  pageTemplates: Record<string, Array<string>>,
  pageNames: Array<string>,
  userJournies: Record<string, Array<string>>,
  downloadTestCase: (testName: string) => void,
  setToastContents: React.Dispatch<React.SetStateAction<{ header: string, body: string, severity?: 'error' | 'info' | 'success' | 'warning' }>>,
  handleXMLFileInput: (target: EventTarget & HTMLInputElement) => void,
  deleteTest: (testToDelete: string) => void,
  setTestName: (oldTestName: string, newName: string, testContents: Array<Array<strippedInputObj>>) => void
}

function TestCaseEditor(props: TestCaseEditorProps) {
  const [pages, setPages] = useState<PageObj[]>([
    { pid: 0, name: "", inputs: [] }
  ]);

  const [newPid, setNewPid] = useState<number>(pages.length);
  const [activePagePid, setActivePagePid] = useState<number>(-1);
  const [activePage, setActivePage] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<PageObj>();
  const [testName, setTestName] = useState<string>('');
  const [testCaseStart, setTestCaseStart] = useState<strippedInputObj>({ id: "", inputType: "testCaseStart", value: "" });
  const [testCaseComplete, setTestCaseComplete] = useState<strippedInputObj>({ id: "", inputType: "testCaseComplete", value: "" });
  const [inputError, setInputError] = useState(false);
  const [editTest, setEditTest] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [activePagePolicyInputs, setActivePagePolicyInputs] = useState<Array<string> | undefined>([]);


  let params = useParams();
  let navigate = useNavigate();

  const setTestPages = (pid: number, newPage: PageObj) => {

    let replaceIx: number = pages.findIndex((e: PageObj) => e.pid === pid);
    let newPages = cloneDeep(pages);
    newPages.splice(replaceIx, 1, newPage);
    setPages(newPages);

  }

  const saveCurrentPageBeforePageChange = (newPageId: number) => {
    let currentIndex = pages.findIndex(pageInArray => pageInArray.pid === activePagePid);
    if (currentPage !== undefined && currentIndex !== -1 && !isEqual(currentPage, pages[currentIndex])) {
      let newPages = cloneDeep(pages);
      newPages.splice(currentIndex, 1, currentPage);
      setPages(newPages);
    }
    setActivePagePid(newPageId);
  }

  const setPage = (pid: number, newPage: PageObj) => {
    setCurrentPage(newPage);
  }; // setPage()

  const formatEditorToRunner = (toFormat: Array<PageObj>): Array<Array<strippedInputObj>> => {
    let newPages: Array<Array<strippedInputObj>> = [];
    setInputError(false);

    for (let i = 0; i < toFormat.length; i++) {
      let newInputs: strippedInputObj[] = [];
      newInputs.push({ id: toFormat[i].name, inputType: 'metadata', value: "" })
      if (i === 0) {
        newInputs.push(testCaseStart);
      }
      for (let j = 0; j < toFormat[i].inputs.length; j++) {
        let { id, inputType, value }: strippedInputObj = toFormat[i].inputs[j];

        if (toFormat[i].inputs[j].policyInput === "function") {
          //var functionInputType = Object.entries(FunctionInputs).find(enumInput => enumInput[0] === pages[i].inputs[j].inputType);
          // Search the enum type so that we can get the value
          inputType = toFormat[i].inputs[j].inputType;
        }
        if (id === "") {
          setInputError(true);
          return [];
        }
        newInputs.push({ id, inputType, value });
      } // for each input

      if (i === toFormat.length - 1) {
        newInputs.push(testCaseComplete);
      }
      newPages.push(newInputs);
    } // for each page

    return newPages
  }

  useEffect(() => {
    const newPagePolicy: Array<string> | undefined = JSON.stringify(props.pageTemplates) !== '{}' ? props.pageTemplates[activePage] : undefined;
    setActivePagePolicyInputs(newPagePolicy);
  }, [activePage])

  const currentPageChange = () => {
    var newActivePage = pages.find((e: PageObj) => e.pid === activePagePid);
      if (newActivePage !== undefined && newActivePage.name !== activePage) {
        setCurrentPage(cloneDeep(newActivePage));
        setActivePage(newActivePage.name);
      }
      else if (newActivePage === undefined) {
        setCurrentPage(undefined);
        setActivePage('');
      }
  }

  useEffect(() => {
    currentPageChange();
  }, [activePagePid])


  useEffect(() => {

    if (!editTest)
    {
      currentPageChange();
    }

  }, [pages])

  const saveTest = () => {
    setInputError(false);
    let newPages = cloneDeep(pages);
    if (testCaseStart.value === '' || testCaseComplete.value === '') {
      props.setToastContents({ header: `Error: ${params.case} not saved`, body: `${params.case} has an invalid Test Definition`, severity: 'error' });
      setInputError(true);
      return;
    }

    if (pages.length === 0) {
      props.setToastContents({ header: `Error: ${params.case} not saved`, body: `${params.case} has no pages defined.`, severity: 'error' });
      setInputError(true);
      return;
    }
    // Reflect test changes into overarching test suite
    if (params.case !== undefined) {
      let currentIndex = pages.findIndex(pageInArray => pageInArray.pid === activePagePid);
      if (currentPage !== undefined && currentIndex !== -1 && !isEqual(currentPage, pages[currentIndex])) {
        newPages.splice(currentIndex, 1, currentPage);
        setPages(newPages);
      }
      let jsonOutput = formatEditorToRunner(newPages);
      if (jsonOutput.length !== 0) {
        if (testName !== params.case) {
          props.setTestName(params.case, testName, jsonOutput);
        }
        else {
          let newTests = cloneDeep(props.tests);
          newTests[params.case] = jsonOutput;
          props.setTests(newTests);
          props.setToastContents({ header: `Saved ${params.case}`, body: `${params.case} was successfully saved` });
        }
      }

      setEditTest(false);
    }
  }

  const loadTest = () => {
    debugger;
    if (params.case !== undefined && props.tests[params.case!] !== undefined && props.tests[params.case!].length > 0) {
      setTestName(params.case);
      let testToView: PageObj[] = [];
      props.tests[params.case].forEach((testPage, pageIndex) => {
        let nextPageObj: PageObj = {
          pid: pageIndex,
          name: testPage[0].id,
          inputs: []
        }
        testPage.forEach((input) => {
          if (input === undefined || input.inputType === undefined || input.inputType === 'metadata') {
            return;
          }
          if (input.inputType === 'testCaseStart') {
            setTestCaseStart({ id: input.id, inputType: "testCaseStart", value: input.value });
            return;
          }
          if (input.inputType === 'testCaseComplete') {
            setTestCaseComplete({ id: input.id, inputType: "testCaseComplete", value: input.value });
            return;
          }
          let currentPolicyInput: "policy" | "function" | "custom";
          if (input.inputType.includes('Fn::')) {
            currentPolicyInput = "function";
          }
          else if (props.policyVariables[input.id] !== undefined) {
            currentPolicyInput = "policy";
          }
          else {
            currentPolicyInput = "custom";
          }
          let currentInputType: InputValues | FunctionInputs = input.inputType as InputValues | FunctionInputs
          nextPageObj.inputs.push({
            iid: nanoid(),
            policyInput: currentPolicyInput,
            id: input.id,
            inputType: currentInputType,
            value: input.value
          })
        })
        testToView.push(nextPageObj);
      })
      setPages([...testToView]);
      setNewPid(testToView.length);
    }
    else if (props.tests[params.case!]?.length === 0) {
      setTestName(params.case!);
      setTestCaseStart({ id: "", inputType: "testCaseStart", value: "" });
      setTestCaseComplete({ id: "", inputType: "testCaseComplete", value: "" });
      setPages([]);
      setNewPid(0);
    }
    else {
      navigate("/test-automation/", { replace: true });
    }

    if (props.tests[params.case!]?.length === 0)
      setEditTest(true);
    else if (!inputError)
      setEditTest(false);
  }

  /*
  if(activePage == null){
    if(pages.length > 0){
      setActivePagePid(pages[0].pid);
      activePage = pages[0];
    }
    else
      activePage = {pid: -1, name: "", inputs: []};
  }
  */

  useEffect(() => {

    var tempPages = [...pages];
    for (var tempPage of tempPages) {
      if (tempPage.name === "" || !props.pageNames.includes(tempPage.name)) {
        tempPage.name = props.pageNames[0];
      }
    }

    setPages([...tempPages]);

  }, [props.pageNames])

  useEffect(() => {
    if (!editTest && (params.case !== testName || !isEqual(props.tests[params.case], pages))) {
      loadTest();
    }
    else if(params.case !== testName){
      loadTest();
    }

  }, [params, props.tests]);

  const selectUserJourney = (journeyName: string) => {
    let newPages: PageObj[] = props.userJournies[journeyName].map((page, index) => {
      let journeyPage: PageObj = {
        pid: index,
        name: page,
        inputs: props.pageTemplates[page].map((inputName, index) => {
          let pageInput: InputObj;

          if (inputName.includes('-Fn::')) {
            let otpValues = props.policyVariables[inputName].id.split(',');
            pageInput = {
              id: otpValues[0],
              inputType: FunctionInputs.OTPEmail,
              policyInput: 'function',
              iid: nanoid(),
              value: otpValues.length > 1 ? otpValues[1] : ""
            }
          }
          else {
            pageInput = {
              id: props.policyVariables[inputName].id,
              inputType: props.policyVariables[inputName].input,
              policyInput: 'policy',
              iid: nanoid(),
              value: props.policyVariables[inputName].restriction !== undefined && props.policyVariables[inputName].restriction!.length > 0 ? props.policyVariables[inputName].restriction![0][0] : ""
            }
          }
          return pageInput;
        })
      }
      return journeyPage;
    });


    setPages([...newPages]);
    setNewPid(newPages.length);
    setActivePagePid(-1);
  }


  return (
    <Grid container className="App mb-5">
      <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'space-between', flexDirection: { xs: 'column-reverse', md: 'row' }, marginBlock: '25px' }}>
        <Box>
          {editTest ? 
          <TextField
            label='Test:'
            id='test-case-name'
            variant='filled'
            onChange={(e) => setTestName(e.target.value)}
            className='align-content-end'
            value={testName} /> :
            <Typography variant='h4'>Test: {testName}</Typography>}
        </Box>
        <Box >
          {editTest ?
            <>
              <Button size='small' onClick={saveTest}>Save Test</Button>
              <Button size='small' className='ms-2' onClick={loadTest}>Discard Changes</Button>
            </> :
            <>
              <Button size='small' onClick={() => setEditTest(true)}>Edit</Button>
              <Tooltip title='Download Test JSON'><IconButton size='small' disabled={props.tests[params.case!] === undefined} onClick={() => props.downloadTestCase(params.case!)} className='ms-2'><DownloadIcon /></IconButton></Tooltip>
            </>
          }
          <Tooltip title='Delete Test'><IconButton size='small' color='error' className='ms-2' onClick={() => setShowDeleteModal(true)}><DeleteIcon /></IconButton></Tooltip>
          <span id="fileHandler" className='ms-2 same-line'>
            <input type="file" name="policyUpload" id="policyUpload" accept='.xml' onChange={e => props.handleXMLFileInput(e.target)} />
            <Tooltip title='Upload your polciy base file which has your user journeys and technical profiles to assist in test case construction.' arrow>
              <Button size='small' variant='outlined' onClick={() => document.getElementById('policyUpload')?.click()}>Upload XML policy</Button>
            </Tooltip>
          </span>
        </Box>
      </Grid>
      <Grid item xs={12} lg={5}>
        <PageList
          pages={pages}
          setPages={setPages}
          newPid={newPid}
          setNewPid={setNewPid}
          activePagePid={activePagePid}
          setActivePagePid={setActivePagePid}
          setTestPages={setTestPages}
          pageNames={props.pageNames}
          journeyNames={props.userJournies}
          selectUserJourney={selectUserJourney}
          validTestBookends={testCaseStart.value !== '' && testCaseComplete.value !== ''}
          pageTemplates={props.pageTemplates}
          editable={editTest}
          saveCurrentPageBeforePageChange={saveCurrentPageBeforePageChange} />
      </Grid>
      {activePagePid === -1 || currentPage === undefined ?
        <Grid item xs={12} lg={7}>
          <TestBookends
            testCaseStart={testCaseStart}
            testCaseComplete={testCaseComplete}
            setTestCaseStart={setTestCaseStart}
            setTestCaseComplete={setTestCaseComplete}
            inputError={inputError}
            editable={editTest} />
        </Grid>
        :
        <Grid item xs={12} lg={7}>
          <PageEditor
            page={currentPage}
            setPage={setPage}
            pagePolicyInputs={activePagePolicyInputs}
            policyVariables={props.policyVariables}
            inputError={inputError}
            editable={editTest} />
        </Grid>
      }
      <Dialog open={showDeleteModal} onClose={() => setShowDeleteModal(false)}>
        <DialogTitle>Confirm Test Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete {params.case}? It cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button color="secondary" onClick={() => { setShowDeleteModal(false); }}>
            No
          </Button>
          <Button variant="contained" color='error' onClick={() => { setShowDeleteModal(false); props.deleteTest(params.case!); }}>
            Yes I am sure
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}

export default TestCaseEditor;